import * as Sentry from "@sentry/browser";
import { tracker as openReplayTracker } from '@openreplay/tracker';
import { updateSentrySchoolCode, updateSentryUsername } from '../sentryAngularJsHandler';

/**
 * UserInfoService is a service class responsible for managing user information.
 * It interacts with HTTP requests, session storage, and various user types.
 */
class UserInfoService {
    /**
     * Constructs the UserInfoService instance.
     * @param {Object} $http - AngularJS HTTP service.
     * @param {Object} $bridges - Service for bridge utilities.
     * @param {Object} $config - Configuration service.
     * @param {Object} Candidate - Candidate model.
     * @param {Object} CMSUser - CMSUser model.
     * @param {Object} $signals - Signal emitter service.
     * @param {Object} $rootScope - AngularJS root scope.
     * @param {Object} $sessionStorage - Session storage service.
     */
    constructor($http, $bridges, $config, Candidate, CMSUser, $signals, $rootScope, $sessionStorage) {
        this.$http = $http;
        this.$bridges = $bridges;
        this.$config = $config;
        this.Candidate = Candidate;
        this.CMSUser = CMSUser;
        this.$signals = $signals;
        this.$rootScope = $rootScope;
        this.$sessionStorage = $sessionStorage;

        this._logIdentifier = 'UserInfoService';
        this._currentUser = null;
        this._currentUUID = null;
        this._response = null;
        this.$logger = $bridges.logger();
    }

    /**
     * Loads user information based on the provided user UUID.
     * @param {string} userUUID - The UUID of the user.
     * @param {Function} onDataLoaded - Callback function when data is successfully loaded.
     * @param {Function} onDataLoadFailed - Callback function when data loading fails.
     * @param {Function} onDataLoadUnauthenticated - Callback function when data loading is unauthenticated.
     */
    loadUserInfo(userUUID, onDataLoaded, onDataLoadFailed, onDataLoadUnauthenticated) {
        this.$logger.logEnter(this._logIdentifier, 'loadUserInfo');

        // Check if the user info is already loaded and matches the UUID
        if (this._currentUser !== null && userUUID === this._currentUUID) {
            onDataLoaded(this._currentUser, userUUID, this._response);
            return;
        }
        if (!userUUID) {
            this.$logger.error(this._logIdentifier, 'Warning, not loading userdata, userUUID is undefined or null');
            if (typeof onDataLoadFailed === 'function') {   
                onDataLoadFailed({error: "userUUID is undefined or null"}, userUUID, null);
            }
            return;
        }
        const url = this.$config.getUserFileURL(userUUID);
        this.$http.get(url).then((response) => {
            this._response = response.data;

            // Check if the response contains a success flag
            if (response.data.hasOwnProperty("success")) {
                if (response.data.success !== true) {
                    this._currentUser = null;
                    if (typeof onDataLoadUnauthenticated === 'function') {
                        onDataLoadUnauthenticated(null, userUUID, response);
                    }
                    return;
                }
            }

            // Adjustments for testing mode
            if (this.$rootScope.isTestingMode) {
                response.data.taskUUID = this.$sessionStorage.usedAssessment;
            }

            // Determine user type and instantiate the appropriate model
            if (response.data.type === "candidate" || response.data.type === "invigilator" || response.data.type === "examiner") {
                this._currentUser = this.Candidate(response.data);
                this.$logger.info(this._logIdentifier, "_currentUser type:", response.data.type, this._currentUser);
                this.setSentryUser(userUUID);
            } else if (response.data.type === "cmsUser") {
                this._currentUser = this.CMSUser(response.data.session);
                this.$logger.info(this._logIdentifier, "_currentUser: cmsuser ", response.data.type, this._currentUser);
            } else {
                this.$logger.error(this._logIdentifier, 'Warning, unknown user type \"' + response.data.type + "\"");
                this._currentUser = response.data;
            }
            this._currentUUID = userUUID;

            this.$signals.emit(IB.Signals.USER_FILE_LOADED, this._currentUser);

            if (typeof onDataLoaded === 'function') {
                onDataLoaded(this._currentUser, userUUID, this._response);
            }
        }, (response) => {
            console.error("Failed to load user data (" + response.status + ")", response.data);
            if (typeof onDataLoadFailed === 'function') {
                onDataLoadFailed(null, userUUID, response);
            }
        });
    }

    /**
     * 
     * @param {string} userUUID 
     */
    setSentryUser(userUUID) {
        let username = this._currentUser.getCandidateNo();
        let specimenName = null;

        // For specimen packages, use "Anon" instead of the dummy candidate code
        try {
            const injector = angular.element(document.body).injector();
            if (injector && injector.has('TaskInfoService')) {
                const taskInfo = injector.get('TaskInfoService').getTaskInfo();
                if (taskInfo && taskInfo.getPackageType() === 'specimen') {
                    const tasks = taskInfo.getTasks();
                    specimenName = (tasks && tasks.length > 0 && tasks[0].title) || taskInfo.getSubjectOrTitle() || 'Unknown';
                    username = 'Anon';
                }
            }
        } catch (e) {
            console.warn('UserInfoService.setSentryUser: Could not determine specimen status:', e.message);
        }

        Sentry.setUser({ id: userUUID, username });
        updateSentryUsername(username, specimenName);
        updateSentrySchoolCode(this._currentUser.getSchoolCode());
        openReplayTracker.setMetadata("candidateNo", this._currentUser.getCandidateNo());
        openReplayTracker.setUserID(userUUID);
    }

    /**
     * Retrieves the current user information.
     * @returns {Object} The current user object.
     */
    getUserInfo() {
        this.$logger.logEnter(this._logIdentifier, 'getUserInfo');
        this.$logger.logExit(this._logIdentifier, 'getUserInfo');
        return this._currentUser;
    }

    /**
     * Sets the current user information.
     * @param {Object} userInfo - The user information to set.
     */
    setUserInfo(userInfo) {
        this._currentUser = userInfo;
    }

    /**
     * Clears the current user information, UUID, and response.
     */
    clear() {
        this._currentUser = null;
        this._currentUUID = null;
        this._response = null;
    }
}

export default UserInfoService;